import React, { useState, useEffect } from 'react';
import useForm from 'hooks/useForm';
import localforage from 'localforage';

// reactstrap components
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  FormGroup,
  Form,
  Input,
  Container,
  Row,
  Col,
  Badge,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'reactstrap';
// core components
import UserHeader from 'components/Headers/UserHeader.js';
import { changePassword } from 'services/user';
import { Notification } from 'notification/notification';
import { useHistory } from 'react-router-dom';
import { getUserInfo, setUserInfoSave } from 'services/user';
import {
  getWhatsappConfig,
  testWhatsappConnection,
  createWhatsappInstance,
  deleteWhatsappInstance,
  getWhatsappQrCode,
} from 'services/whatsapp';

const initialStatePassword = {
  oldPassword: '',
  newPassword: '',
};

const initialStateWhatsapp = {
  instanceStatus: null,
};

const initialStateUserInfo = {
  id: 0,
  email: '',
  lastName: '',
  firstName: '',
  phoneNumber: '',
};

const Profile = () => {
  const history = useHistory();
  const [userInfo, setUserInfo] = useState(initialStateUserInfo);
  const { handleChange, values } = useForm(initialStatePassword);
  const [whatsappConfig, setWhatsappConfig] = useState(initialStateWhatsapp);
  const [testingConnection, setTestingConnection] = useState(false);
  const [creatingInstance, setCreatingInstance] = useState(false);
  const [removingInstance, setRemovingInstance] = useState(false);
  const [qrModal, setQrModal] = useState(false);
  const [qrCode, setQrCode] = useState(null);
  const [pollingQr, setPollingQr] = useState(false);
  const qrModalOpenedAt = React.useRef(null);

  useEffect(() => {
    async function getData() {
      const data = await getUserInfo();
      if (data !== undefined) {
        setUserInfo(data);
      }
      const wConfig = await getWhatsappConfig();
      if (wConfig) {
        setWhatsappConfig(wConfig);
      }
    }
    getData();
  }, []);

  const setUserInfoDetail = (key, value) => {
    setUserInfo({
      ...userInfo,
      [key]: value,
    });
  };

  const handleChangeUserInfo = (e) => {
    setUserInfoDetail(e.target.getAttribute('name'), e.target.value);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    const status = await changePassword(values);
    if (status === 200) {
      await localforage.removeItem('/auth/login');
      history.push('/login');
      return;
    }
    Notification(status);
    return;
  };

  const handleChangeUser = async (e) => {
    e.preventDefault();
    const status = await setUserInfoSave(userInfo);
    if (status === 200) {
      Notification(status);
      return;
    }
  };

  const handleTestConnection = async () => {
    setTestingConnection(true);
    const connected = await testWhatsappConnection();
    setTestingConnection(false);
    if (connected) {
      setWhatsappConfig((prev) => ({ ...prev, instanceStatus: 'CONNECTED' }));
      Notification(200, 'Instância conectada com sucesso!');
    } else {
      setWhatsappConfig((prev) => ({
        ...prev,
        instanceStatus: 'DISCONNECTED',
      }));
      Notification(
        500,
        'Não foi possível conectar. Verifique os dados da instância.',
      );
    }
  };

  const openQrModal = async () => {
    setQrCode(null);
    qrModalOpenedAt.current = Date.now();
    setQrModal(true);
    const qr = await getWhatsappQrCode();
    if (qr) setQrCode(qr);
    setPollingQr(true);
  };

  const handleDeleteInstance = async () => {
    if (
      !window.confirm(
        'Tem certeza? Isso removerá a instância da Evolution API e você precisará reconectar o dispositivo.',
      )
    )
      return;
    setRemovingInstance(true);
    const ok = await deleteWhatsappInstance();
    setRemovingInstance(false);
    if (ok) {
      setWhatsappConfig((prev) => ({ ...prev, instanceStatus: null }));
    }
  };

  const handleCreateInstance = async () => {
    setCreatingInstance(true);
    const ok = await createWhatsappInstance();
    setCreatingInstance(false);
    if (ok) {
      openQrModal();
    } else {
      Notification(500, 'Erro ao criar instância. Verifique as configurações.');
    }
  };

  // Atualiza QR a cada 5s
  React.useEffect(() => {
    if (!pollingQr || !qrModal) return;
    const qrInterval = setInterval(async () => {
      const qr = await getWhatsappQrCode();
      if (qr) setQrCode(qr);
    }, 5000);
    return () => clearInterval(qrInterval);
  }, [pollingQr, qrModal]);

  // Verifica conexão a cada 15s, mas só após 20s do modal abrir
  React.useEffect(() => {
    if (!pollingQr || !qrModal) return;
    const connInterval = setInterval(async () => {
      const elapsed = Date.now() - (qrModalOpenedAt.current || 0);
      if (elapsed < 20000) return;
      const connected = await testWhatsappConnection();
      if (connected) {
        setWhatsappConfig((prev) => ({ ...prev, instanceStatus: 'CONNECTED' }));
        setPollingQr(false);
        setQrModal(false);
        Notification(200, 'WhatsApp conectado com sucesso!');
      }
    }, 15000);
    return () => clearInterval(connInterval);
  }, [pollingQr, qrModal]);

  const handleCloseQrModal = () => {
    setQrModal(false);
    setPollingQr(false);
    setQrCode(null);
  };

  const whatsappStatusBadge = () => {
    switch (whatsappConfig.instanceStatus) {
      case 'CONNECTED':
        return (
          <Badge color='success' className='ml-2'>
            Conectado
          </Badge>
        );
      case 'CONNECTING':
        return (
          <Badge color='warning' className='ml-2'>
            Conectando...
          </Badge>
        );
      case 'DISCONNECTED':
        return (
          <Badge color='danger' className='ml-2'>
            Desconectado
          </Badge>
        );
      default:
        return (
          <Badge color='secondary' className='ml-2'>
            Não configurado
          </Badge>
        );
    }
  };

  return (
    <>
      <UserHeader name={userInfo.firstName} />
      <Container className='mt--7' fluid>
        <Row>
          <Col className='order-xl-1' xl=''>
            <Card className='bg-secondary shadow'>
              <CardHeader className='bg-white border-0'>
                <Row className='align-items-center'>
                  <Col xs='8'>
                    <h3 className='mb-0'>Meus dados</h3>
                  </Col>
                  <Col className='text-right' xs='4'>
                    <Button
                      color='primary'
                      onClick={handleChangeUser}
                      size='sm'
                    >
                      Salvar
                    </Button>
                  </Col>
                </Row>
              </CardHeader>
              <CardBody>
                <Form>
                  <h6 className='heading-small text-muted mb-4'>
                    Informações de Usuário
                  </h6>
                  <div className='pl-lg-4'>
                    <Row>
                      <Col lg='6'>
                        <FormGroup>
                          <label
                            className='form-control-label'
                            htmlFor='input-first-name'
                          >
                            Nome
                          </label>
                          <Input
                            className='form-control-alternative'
                            id='input-first-name'
                            placeholder='First name'
                            type='text'
                            name='firstName'
                            onChange={handleChangeUserInfo}
                            value={userInfo.firstName}
                          />
                        </FormGroup>
                      </Col>
                      <Col lg='6'>
                        <FormGroup>
                          <label
                            className='form-control-label'
                            htmlFor='input-last-name'
                          >
                            Sobrenome
                          </label>
                          <Input
                            className='form-control-alternative'
                            id='input-last-name'
                            placeholder='Last name'
                            type='text'
                            name='lastName'
                            onChange={handleChangeUserInfo}
                            value={userInfo.lastName}
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row>
                      <Col lg='6'>
                        <FormGroup>
                          <label
                            className='form-control-label'
                            htmlFor='input-email'
                          >
                            Endereço de e-mail:
                          </label>
                          <Input
                            className='form-control-alternative'
                            id='input-email'
                            placeholder='jesse@example.com'
                            type='email'
                            name='email'
                            onChange={handleChangeUserInfo}
                            value={userInfo.email}
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                  </div>
                  <hr className='my-4' />
                  {/* Address */}
                  <h6 className='heading-small text-muted mb-4'>
                    Informações de Contato
                  </h6>
                  <div className='pl-lg-4'>
                    <Row>
                      <Col md='4'>
                        <FormGroup>
                          <label
                            className='form-control-label'
                            htmlFor='input-tel'
                          >
                            Telefone
                          </label>
                          <Input
                            className='form-control-alternative'
                            id='input-address'
                            placeholder='(11) 99999-9999'
                            type='tel'
                            pattern='(\(\d{2}\)\s)?(\d{4,5}\-\d{4})'
                            name='phoneNumber'
                            onChange={handleChangeUserInfo}
                            value={userInfo.phoneNumber}
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                  </div>
                  <hr className='my-4' />
                  {/* Address */}
                  <h6 className='heading-small text-muted mb-4'>
                    Credenciais
                    <Button
                      className='float-right'
                      color='default'
                      onClick={handleChangePassword}
                      size='sm'
                    >
                      Atualizar Senha
                    </Button>
                  </h6>

                  <div className='pl-lg-4'>
                    <Row>
                      <Col md='6'>
                        <FormGroup>
                          <label
                            className='form-control-label'
                            htmlFor='input-old-password'
                          >
                            Senha Antiga
                          </label>
                          <Input
                            className='form-control-alternative'
                            id='input-old-passowrd'
                            placeholder='*************'
                            type='password'
                            name='oldPassword'
                            onChange={handleChange}
                          />
                        </FormGroup>
                      </Col>
                      <Col md='6'>
                        <FormGroup>
                          <label
                            className='form-control-label'
                            htmlFor='input-new-password'
                          >
                            Senha Nova
                          </label>
                          <Input
                            className='form-control-alternative'
                            id='input-new-passowrd'
                            placeholder='*************'
                            type='password'
                            name='newPassword'
                            onChange={handleChange}
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                  </div>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
        <Row className='mt-4'>
          <Col className='order-xl-1' xl=''>
            <Card className='bg-secondary shadow'>
              <CardHeader className='bg-white border-0'>
                <Row className='align-items-center'>
                  <Col xs='8'>
                    <h3 className='mb-0'>
                      WhatsApp
                      {whatsappStatusBadge()}
                    </h3>
                  </Col>
                  <Col
                    className='text-right'
                    style={{ display: 'flex' }}
                    xs='4'
                  >
                    {whatsappConfig.instanceStatus === 'CONNECTING' ? (
                      <Button
                        color='warning'
                        onClick={openQrModal}
                        size='sm'
                        className='mr-2'
                      >
                        Exibir QR Code
                      </Button>
                    ) : (
                      <Button
                        color='warning'
                        onClick={handleCreateInstance}
                        size='sm'
                        disabled={creatingInstance}
                        className='mr-2'
                      >
                        {creatingInstance ? (
                          <>
                            <Spinner size='sm' /> Conectando...
                          </>
                        ) : (
                          'Conectar Dispositivo'
                        )}
                      </Button>
                    )}
                    <Button
                      color='success'
                      onClick={handleTestConnection}
                      size='sm'
                      disabled={testingConnection}
                      className='mr-2'
                    >
                      {testingConnection ? (
                        <>
                          <Spinner size='sm' /> Testando...
                        </>
                      ) : (
                        'Testar Conexão'
                      )}
                    </Button>
                    <Button
                      color='danger'
                      onClick={handleDeleteInstance}
                      size='sm'
                      disabled={removingInstance}
                      title='Remove a instância da Evolution API e desconecta o dispositivo'
                    >
                      {removingInstance ? (
                        <>
                          <Spinner size='sm' /> Removendo...
                        </>
                      ) : (
                        'Remover Dispositivo'
                      )}
                    </Button>
                  </Col>
                </Row>
              </CardHeader>
              <CardBody>
                <p className='text-muted mb-0' style={{ fontSize: '14px' }}>
                  Clique em <strong>Conectar Dispositivo</strong> para gerar o
                  QR code e escanear com o WhatsApp do celular.
                </p>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* QR Code Modal */}
      <Modal isOpen={qrModal} toggle={handleCloseQrModal} centered>
        <ModalHeader toggle={handleCloseQrModal}>Conectar WhatsApp</ModalHeader>
        <ModalBody className='text-center'>
          <p className='text-muted mb-3'>
            Abra o WhatsApp no celular &rarr;{' '}
            <strong>Dispositivos conectados</strong> &rarr;{' '}
            <strong>Conectar dispositivo</strong> e escaneie o QR code abaixo.
          </p>
          {qrCode ? (
            <img
              src={qrCode}
              alt='QR Code WhatsApp'
              style={{ maxWidth: '280px', width: '100%', borderRadius: '8px' }}
            />
          ) : (
            <div className='py-4'>
              <Spinner
                color='success'
                style={{ width: '3rem', height: '3rem' }}
              />
              <p className='mt-3 text-muted'>Aguardando QR code...</p>
            </div>
          )}
          <p className='mt-3 text-muted' style={{ fontSize: '12px' }}>
            O QR code é atualizado a cada 5 segundos. A conexão é detectada
            automaticamente após o escaneamento.
          </p>
        </ModalBody>
        <ModalFooter>
          <Button color='secondary' onClick={handleCloseQrModal}>
            Fechar
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default Profile;
